// Шапка листа (README «Каркас экрана»; contracts/ui.md, «Каркас»): слева «Скрыть меню [» (до
// 900 px — «Открыть меню [») и крошки; справа по порядку «Найти или перейти…» с ⌘K (не на Mac —
// Ctrl+K; в подсказке — и `/`, сразу поиск), колокольчик с числом непрочитанных, «Создать ▾» и
// служебное — пометка «Замещаете: …». Кнопки-акцента в шапке нет. До 640 px поиск, уведомления
// и «Создать» живут в нижней навигации, пометка о замещении — полосой под шапкой. Крошки страница
// может заменить своим путём со ссылками (протокол: путь назад к папкам «Документов»).
//
// Строка документа (ux-dense, 25.10.2026): шире 640 px страница может занять и место действий —
// справа перед общим поиском (протокол: «В сети», «Выгрузить ▾», «Зафиксировать неделю» — акцент
// экрана, «Во весь экран»). Тогда общий поиск — значком, палитра по ⌘K — как раньше. Без
// страницы-владельца шапка прежняя.
//
// 030 (T019, T059; FR-031, FR-032; ASG-DESK-15; конституция X): у сеанса по ссылке шапка та же —
// «Найти или перейти…» (палитра его разделов) и уведомления; «Создать ▾» скрыт, как у любого, кому
// нечего создавать. «Помощь» — в шапке последней, пока меню выезжает поверх экрана (641–900 px):
// на каждом экране она видна без открытия меню. Шире 900 px «Помощь» — в меню, до 640 px —
// в нижней навигации. До 640 px шапка сеанса по ссылке — «Меню → раздел → поиск → уведомления»
// (ux.md, раздел 15): в его нижней навигации поиска и уведомлений нет; лента — окном снизу.
//
// 032 (T026; FR-002, FR-003): число у колокольчика — непрочитанные «требует действия», как
// у «Входящих» в меню; их нет, а непрочитанные есть — точка без числа (BellCount).
import {
  Button,
  Icon,
  IconButton,
  Kbd,
  ShortcutKeys,
  Tooltip,
  cx,
  media,
  modShortcut,
  useIsPhone,
  useMediaQuery,
} from '@kp/ui';
import { Bell, LifeBuoy, Menu, PanelLeftClose, PanelLeftOpen, Search } from '@kp/ui/icons';
import { useMatches } from '@tanstack/react-router';
import type { ButtonHTMLAttributes, Ref } from 'react';
import { useShell } from './context.tsx';
import { CreateMenu } from './create.tsx';
import {
  BellCount,
  NotificationsPopover,
  bellLabel,
  useInboxCounts,
} from './NotificationsFeed.tsx';
import { useMe } from './session.ts';
import { SubstitutionMark } from './SubstitutionMark.tsx';

export function Topbar() {
  const shell = useShell();
  const overlayMenu = useMediaQuery(media.tablet);
  const isPhone = useIsPhone();
  const { linkSession } = useMe();

  // Действия страницы — только шире 640 px: до 640 px у страницы своя раскладка.
  const { pageActions: claimed } = shell;
  const pageActions = claimed && !isPhone;

  return (
    <header className={cx('kp-topbar', pageActions && 'has-page')}>
      {overlayMenu ? (
        <IconButton
          icon={Menu}
          label="Открыть меню"
          shortcut="["
          onClick={() => shell.setMenuOpen(true)}
        />
      ) : (
        <IconButton
          icon={shell.menuCollapsed ? PanelLeftOpen : PanelLeftClose}
          label={shell.menuCollapsed ? 'Показать меню' : 'Скрыть меню'}
          shortcut="["
          onClick={shell.toggleMenu}
        />
      )}
      <Breadcrumbs />
      <span className="sp" />
      {pageActions ? <PageActionsSlot /> : null}
      {isPhone ? (
        linkSession ? (
          <>
            <SearchButton onOpen={() => shell.setPaletteOpen(true)} />
            <BellButton onClick={() => shell.setNotificationsOpen(true)} />
          </>
        ) : null
      ) : (
        <>
          <SearchButton compact={pageActions} onOpen={() => shell.setPaletteOpen(true)} />
          <NotificationsPopover trigger={<BellButton />} />
          <CreateMenu />
          <SubstitutionMark variant="topbar" />
          {linkSession && overlayMenu ? (
            <Button variant="ghost" icon={LifeBuoy} onClick={() => shell.openHelp()}>
              Помощь
            </Button>
          ) : null}
        </>
      )}
    </header>
  );
}

/** Место действий страницы справа перед общим поиском (протокол: строка документа). */
function PageActionsSlot() {
  const { setActionsSlot } = useShell();
  return <div ref={setActionsSlot} className="kp-topbar-page" />;
}

/**
 * Крошки маршрута — подписи staticData.crumb. Путь, который рисует страница (useCrumbSlot:
 * протокол — «Документы / папка / … / протокол» ссылками), встаёт на их место: узел — обёртка,
 * навигацию «Путь» страница рисует сама (рядом с путём протокол ставит заголовок и пометки).
 */
function Breadcrumbs() {
  const { pageCrumbs, setCrumbSlot } = useShell();
  const crumbs = useMatches({
    select: (matches) =>
      matches.map((match) => match.staticData.crumb).filter((crumb) => crumb !== undefined),
  });
  if (pageCrumbs) return <div ref={setCrumbSlot} className="kp-crumbs kp-page-crumbs" />;
  if (crumbs.length === 0) return null;
  return (
    <nav aria-label="Путь" className="kp-crumbs">
      <ol>
        {crumbs.map((crumb, index) => (
          <li
            key={`${index}-${crumb}`}
            aria-current={index === crumbs.length - 1 ? 'page' : undefined}
          >
            {crumb}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/**
 * Поле «Найти или перейти…»; до 1100 px сжимается до иконки. Открывает палитру (T050). Клавиша —
 * по платформе: ⌘K на Mac, Ctrl+K на остальных; работают обе. В подсказке и `/` — палитра сразу
 * в поиске (ux.md, раздел 8: клавиша — рядом с действием). compact — значок на любой ширине:
 * место в шапке заняли действия страницы.
 */
function SearchButton({ onOpen, compact = false }: { onOpen: () => void; compact?: boolean }) {
  const shortcut = modShortcut('K');
  return (
    <Tooltip
      content={
        <>
          Найти или перейти <ShortcutKeys shortcut={shortcut} /> · поиск{' '}
          <ShortcutKeys shortcut="/" />
        </>
      }
    >
      <button
        type="button"
        className={cx('kp-search', compact && 'is-compact')}
        aria-label="Найти или перейти"
        aria-keyshortcuts="Meta+K Control+K /"
        onClick={onOpen}
      >
        <Icon icon={Search} />
        <span className="kp-search-text" aria-hidden="true">
          Найти или перейти…
        </span>
        <Kbd aria-hidden="true" className="kp-search-kbd">
          {shortcut}
        </Kbd>
      </button>
    </Tooltip>
  );
}

/**
 * Колокольчик с числом «требует действия» (032). Принимает обработчики и ref от панели
 * уведомлений: он же её открывает.
 */
function BellButton({
  ref,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { ref?: Ref<HTMLButtonElement> }) {
  const counts = useInboxCounts();
  const label = bellLabel(counts);
  return (
    <Tooltip content={label}>
      <button ref={ref} type="button" {...rest} className="t-ibtn kp-bell" aria-label={label}>
        <Icon icon={Bell} />
        <BellCount counts={counts} />
      </button>
    </Tooltip>
  );
}
